import { useLocation } from "react-router";
import { useRef, useEffect, useState, useMemo } from "react";

interface RouteTransitionProps {
  children: React.ReactNode;
  mode?: "parent" | "child";
}

export function RouteTransition({
  children,
  mode = "parent",
}: RouteTransitionProps) {
  const location = useLocation();
  const isFirstMount = useRef(true);
  const prevPathRef = useRef(location.pathname);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Use a stable reference for the key to prevent unnecessary remounts
  const stableKey = useMemo(() => {
    // For the first render, don't use a key at all
    if (isFirstMount.current) {
      return undefined;
    }

    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    // If we're on the same path, don't change the key
    if (currentPath === prevPath) {
      return prevPathRef.current;
    }

    // For actual route changes, use the path as the key
    return currentPath;
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    // Skip if the path hasn't actually changed
    if (currentPath === prevPath && !isFirstMount.current) {
      return;
    }

    const currentPathParts = currentPath.split("/");
    const prevPathParts = prevPath.split("/");

    // Update prevPath for next comparison
    prevPathRef.current = currentPath;

    // Skip animation on first mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    // For parent mode, animate if:
    // 1. We're changing top-level routes (depth <= 2)
    // 2. We're navigating to/from worker or project pages
    const shouldAnimateTransition =
      mode === "parent"
        ? currentPathParts.length <= 2 && prevPathParts.length <= 2
        : currentPathParts.length === prevPathParts.length &&
          currentPathParts.length > 2;

    setShouldAnimate(shouldAnimateTransition);
  }, [location.pathname, mode]);

  // Use a stable wrapper component to prevent unnecessary remounts
  const StableWrapper = useMemo(() => {
    // Only use a key when we actually want to trigger a remount
    const shouldUseKey = !isFirstMount.current && shouldAnimate;

    return (
      <div
        key={shouldUseKey ? stableKey : undefined}
        className={`h-full ${shouldAnimate ? "page-transition" : ""}`}
      >
        {children}
      </div>
    );
  }, [children, shouldAnimate, stableKey]);

  return StableWrapper;
}
